import { SalesOrderDeliveryType } from '../constants/sales-order-delivery-type/index.js';
import { salesOrderStatusName } from '../constants/sales-order-status-name.js';
import { formatBusinessDateFromIso } from '../dates/format-from-iso.js';
import { formatCurrency } from '../format-currency.js';
import { isSalesOrderBalancePaid } from '../sales-order-payments/is-sales-order-balance-paid.js';
import { dateTimeOrNotSet } from './date-time-or-not-set.js';
import { NOT_SET } from './not-set.js';
import { orNotSet } from './or-not-set.js';
import { salesOrderApprovedLine } from './sales-order-next-step-approved-line.js';
import { UNSETTLED_ORDER_FIX_MESSAGE } from './unsettled-order-fix-message.js';
function dateOrNotSet(iso) {
    return formatBusinessDateFromIso(iso) ?? NOT_SET;
}
function formatBalanceToCollect(balanceDue) {
    const amount = Number(balanceDue);
    if (!Number.isFinite(amount))
        return NOT_SET;
    // WHY: an overpayment is "unapplied", never a negative amount to collect.
    return formatCurrency(Math.max(amount, 0)) ?? NOT_SET;
}
// A Credit Return's open balance is credit the customer is owed, closed by a refund or a waive.
function shippedMessage(input) {
    const amount = formatBalanceToCollect(input.balanceDue);
    if (input.path === 'CreditReturn') {
        return `Customer credit ${amount} open. Refund it or waive it to close.`;
    }
    if (input.awaitingClose === false && isSalesOrderBalancePaid(input.balanceDue)) {
        return `Paid, but an invoice is still open. ${UNSETTLED_ORDER_FIX_MESSAGE}`;
    }
    return `Collect ${amount} to close.`;
}
function completedHow(input) {
    if (input.path !== 'CreditReturn')
        return 'Paid in full';
    if (input.creditReturnSettlement === 'WAIVED')
        return 'Credit waived';
    return input.creditReturnSettlement === 'NOTHING_OWED' ? 'No credit owed' : 'Refunded';
}
function acceptedMessage(path) {
    if (path === 'DropShip')
        return 'Waiting on vendor PO receipt.';
    if (path === 'CreditReturn') {
        return "Mark it Shipped when it's on its way back, or receive it in Receiving.";
    }
    return 'Accepted — pick in progress.';
}
// Shipped is en route, so nothing is owed yet; the payment line starts at Delivered.
function shippedInTransitMessage(path) {
    return path === 'CreditReturn'
        ? 'On its way back. Receive it in Receiving.'
        : 'Out for delivery. Mark it Delivered when it arrives.';
}
function pickProgressLine(pick) {
    const percent = pick.totalLines > 0 ? Math.round((pick.committedLines / pick.totalLines) * 100) : 0;
    return {
        text: `Pick ${pick.num} · ${pick.committedLines} of ${pick.totalLines} lines picked`,
        percent: Math.min(Math.max(percent, 0), 100),
    };
}
function pickProgressOf(input) {
    const { pick } = input;
    if (input.status !== 'ACCEPTED' || input.path !== 'Standard' || !pick)
        return undefined;
    return pickProgressLine(pick);
}
function inParallelSteps(input) {
    return (input.status === 'DELIVERY_SCHEDULING' &&
        input.path === 'Standard' &&
        input.readyToDeliverMode === 'auto');
}
function checklistOf(input) {
    if (!inParallelSteps(input))
        return undefined;
    const { pick } = input;
    const pickLine = !pick
        ? []
        : pick.finished
            ? [{ text: 'Pick finished', done: true }]
            : [{ ...pickProgressLine(pick), done: false }];
    const deliveryLine = input.deliveryScheduled
        ? { text: deliveryDateMessage(input), done: true }
        : { text: 'Delivery not scheduled', done: false };
    return [...pickLine, deliveryLine];
}
function deliverySchedulingMessage(input) {
    const bothDone = input.pick?.finished === true && input.deliveryScheduled === true;
    if (inParallelSteps(input) && !bothDone)
        return 'Picking and delivery scheduling in progress.';
    return deliveryDateMessage(input);
}
function approvalTriggerCallout(trigger) {
    const detail = trigger.detail?.trim();
    return detail ? `${trigger.name} — ${detail}` : trigger.name;
}
function deliveryAssignee(input) {
    if (input.deliveryTypeId === SalesOrderDeliveryType.PICK_UP)
        return 'Pick Up';
    if (input.deliveryTypeId === SalesOrderDeliveryType.INTERNAL)
        return orNotSet(input.driverName);
    return orNotSet(input.carrierName);
}
function deliveryDateMessage(input) {
    return `Delivery date: ${dateOrNotSet(input.shipDate)} · ${deliveryAssignee(input)}`;
}
function readyToDeliverMessage(input) {
    const date = dateOrNotSet(input.shipDate);
    if (input.deliveryTypeId === SalesOrderDeliveryType.PICK_UP)
        return `Ready for pickup ${date}`;
    return `Ready to deliver ${date} · ${deliveryAssignee(input)}`;
}
function messageFor(input) {
    switch (input.status) {
        case 'DRAFT':
            return 'Draft — nothing is reserved yet.';
        case 'AWAITING_APPROVAL':
            return input.approval ? 'Decision recorded — updating…' : 'Needs approval';
        case 'SUBMITTED':
            return 'Waiting to be accepted.';
        case 'ACCEPTED':
            return acceptedMessage(input.path);
        case 'DELIVERY_SCHEDULING':
            return deliverySchedulingMessage(input);
        case 'READY_TO_DELIVER':
            return readyToDeliverMessage(input);
        case 'SHIPPED':
            return shippedInTransitMessage(input.path);
        case 'DELIVERED':
            return shippedMessage(input);
        case 'COMPLETE':
            return `${completedHow(input)} ${dateOrNotSet(input.completedOn)} — completed automatically.`;
        case 'CANCELLED':
        case 'REJECTED':
            return `${salesOrderStatusName[input.status]} — ${orNotSet(input.terminal?.reason)}`;
    }
}
function terminalLine(input) {
    const { terminal } = input;
    if (!terminal || (input.status !== 'CANCELLED' && input.status !== 'REJECTED'))
        return undefined;
    if (!terminal.by?.trim() && !terminal.at)
        return undefined;
    return `${orNotSet(terminal.by)} · ${dateTimeOrNotSet(terminal.at)}`;
}
// RS-2: the failure's reason stays on screen until the order is scheduled again.
function failedDeliveryLine(input) {
    const reason = input.failedDeliveryReason?.trim();
    if (input.status !== 'DELIVERY_SCHEDULING' || !reason)
        return undefined;
    return `Delivery failed — ${reason}. Schedule a new delivery.`;
}
function contextLinesFor(input) {
    return [terminalLine(input), failedDeliveryLine(input), salesOrderApprovedLine(input)].filter((line) => line !== undefined);
}
/** The one-line next-step message, callouts and context for an order's status; shared by the detail page and the list. */
export function salesOrderNextStep(input) {
    return {
        message: messageFor(input),
        callouts: input.status === 'AWAITING_APPROVAL'
            ? input.approvalTriggers.map(approvalTriggerCallout)
            : [],
        contextLines: contextLinesFor(input),
        pickProgress: pickProgressOf(input),
        checklist: checklistOf(input),
    };
}
//# sourceMappingURL=sales-order-next-step.js.map